import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useGroupChatContext } from "../../../../../../context/groupchatContext";
import MainPageDialog from "../../../../../../components/dialog";
import { toast } from "react-toastify";
import { locationsApi, useGetLocationConfigurationQuery, useSaveLocationConfigurationMutation } from "../../../../../../services/location_services";
import ActionDialog from "../../../../../../components/actionDialog";
import { getCurrentEpochTime, validateIPInput } from "../../../../../../utils/common_lib";
import { IoMdInformationCircleOutline } from "react-icons/io";
import LocationGroupNurses from "./LocationGroupNurses";
import LocationGroupUsers from "./LocationGroupUsers";
import { IoIosClose } from "react-icons/io";
import { PlusIcon } from "@heroicons/react/20/solid";
import { InputField } from "../../../../../../components/reactHookFormFields/inputField";
import { useForm, useWatch } from "react-hook-form";
import { BsCheckLg } from "react-icons/bs";
import { useGetRestrictedIpsQuery, useAddRestrictedIpMutation, useDeleteRestrictedIpMutation } from "../../../../../../services/restrictedIpService";

const defaultLocationConfiguration = {
  show_vezo_session: 0,
  show_appointment: 0,
  show_allergies: 0,
  show_medication: 0,
};

const locationConfigurationOptions = [
  { key: "show_vezo_session", label: "Vezo Session" },
  { key: "show_appointment", label: "Appointment" },
  { key: "show_allergies", label: "Allergies" },
  { key: "show_medication", label: "Medications" },
];

const toFlag = (value) => (value == 1 || value === true ? 1 : 0);

function CareCoSettingsDialogue({
  refetchLocations = () => {},
  setSelectedLocation = () => {},
  selectedLocation = {},
  providersData = [],
  locationsData = [],
}) {
  const scrollToTop = useCallback(() => {
    const element = document.getElementById('main-dialog-content');
    if (element) {
      element.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  const footerList = useMemo(() => [
    {
      title: "Close",
      action: () =>
        setSelectedLocation({
          showSettingsDialogue: false,
          currentLocation: {},
          settingsType: "",
        }),
      type: "cancel",
    },
  ], []);

  // form
  const { control, formState: { errors }, handleSubmit, setValue } = useForm();
  const ipWatcher = useWatch({ control, name: "ip" });

  const { authData } = useGroupChatContext();
  const { currentLocation } = selectedLocation;

  // states
  const [loadingEnableNursingHome, setLoadingEnableNursingHome] = useState(false);
  const [nursingHomeChatEnabled, setNursingHomeChatEnabled] = useState(currentLocation?.careco_chat_enabled);
  const [showTurnOffGroupDialogue, setShowTurnOffGroupDialogue] = useState(false);
  const [loadingDisableNursingHome, setLoadingDisableNursingHome] = useState(false);
  const [showAddIPField, setShowAddIPField] = useState(false);
  const [selectedIp, setSelectedIp] = useState(null);
  const [locationConfiguration, setLocationConfiguration] = useState(defaultLocationConfiguration);

  // triggers
  // const [enableNursingHomeTrigger] = locationsApi.endpoints.enableNursingHome.useLazyQuery();
  // const [disableNursingHomeTrigger] = locationsApi.endpoints.disableNursingHome.useLazyQuery();
  const [enabledisableNursingHomeTrigger] = locationsApi.endpoints.enableDisableNursingHome.useLazyQuery();
  const [addRestrictedIpTrigger, { isLoading: addingIp }] = useAddRestrictedIpMutation();
  const [deleteRestrictedIpTrigger, { isLoading: deletingIp }] = useDeleteRestrictedIpMutation(); 
  const { data: restrictedIpsList } = useGetRestrictedIpsQuery({ location_id: currentLocation?.id });
  const { data: locationConfigurationData, isFetching: fetchingLocationConfiguration } = useGetLocationConfigurationQuery(
    { location_id: currentLocation?.id },
    { skip: !currentLocation?.id }
  );
  const [saveLocationConfigurationTrigger, { isLoading: savingLocationConfiguration }] = useSaveLocationConfigurationMutation();

  useEffect(() => {
    const configData = locationConfigurationData?.data || {};
    setLocationConfiguration({
      show_vezo_session: toFlag(configData?.show_vezo_session),
      show_appointment: toFlag(configData?.show_appointment),
      show_allergies: toFlag(configData?.show_allergies),
      show_medication: toFlag(configData?.show_medication),
    });
  }, [locationConfigurationData]);

  const handleLocationConfigurationChange = useCallback((key, checked) => {
    setLocationConfiguration((prev) => ({
      ...prev,
      [key]: checked ? 1 : 0,
    }));
  }, []);

  const handleSaveLocationConfiguration = useCallback(async () => {
    try {
      const res = await saveLocationConfigurationTrigger({
        location_id: currentLocation?.id,
        show_medication: toFlag(locationConfiguration.show_medication),
        show_allergies: toFlag(locationConfiguration.show_allergies),
        show_appointment: toFlag(locationConfiguration.show_appointment),
        show_vezo_session: toFlag(locationConfiguration.show_vezo_session),
      });

      const { code, msg } = res?.data || {};
      if (code === 0 || code === "0") {
        toast.success(msg || "Location configuration saved successfully");
      } else {
        toast.error(msg || "Failed to save location configuration");
      }
    } catch (error) {
      console.error("Error saving location configuration", error);
      toast.error("Error saving location configuration");
    }
  }, [currentLocation?.id, locationConfiguration, saveLocationConfigurationTrigger]);

  const onSubmit = useCallback(async () => {
    if(addingIp || deletingIp) {
      return;
    }
    try {
      await addRestrictedIpTrigger({
        location_id: currentLocation?.id,
        ip: ipWatcher,
      })
        .then((res) => {})
        .catch((err) => {
          console.log("Error in adding restricted ip", err);
          toast.error("Error in adding restricted ip");
        })
        .finally(() => {
          setShowAddIPField(false);
          setValue("ip", "");
        });
    } catch (error) {
      console.error("Error adding restricted ip", error);
      toast.error("Error adding restricted ip");
    }
  }, [addRestrictedIpTrigger, currentLocation, ipWatcher, addingIp, deletingIp]);

  const handleDeleteRestrictedIp = useCallback(async (restrictedIp) => {
    if(addingIp || deletingIp) {
      return;
    }
      try {
        await deleteRestrictedIpTrigger({
          location_id: currentLocation?.id,
          id: restrictedIp?.id,
          ip: restrictedIp?.ip,
        })
          .then((res) => {
            toast.success("The selected IP address has been removed.");
          })
          .catch((err) => {
            console.log("Error in deleting restricted ip", err);
            toast.error("Error in deleting restricted ip");
          })
          .finally(() => {
            setSelectedIp(null);
          });
      } catch (error) {
        console.error("Error deleting restricted ip", error);
        toast.error("Error deleting restricted ip");
      }
    }, [currentLocation, deleteRestrictedIpTrigger, addingIp, deletingIp]);

  const handleOpenTurnOffGroupDialogue = () => {
    if (nursingHomeChatEnabled) {
      setShowTurnOffGroupDialogue(true);
    } else {
      setLoadingEnableNursingHome(true);
      try {
        enabledisableNursingHomeTrigger({
          location_id: currentLocation?.id,
          type:"careco",
          enable: 1
        })
          .then((res) => {
            if (res?.data?.data?.location?.Location?.careco_chat_enabled == "1") {
              refetchLocations();
              toast.success("Nursing Home enabled successfully");
              setNursingHomeChatEnabled(true);
            } else {
              toast.error("Error in enabling nursing home");
            }
          })
          .catch((err) => {
            console.log("Error in api calling enable nursing home", err);
            toast.error("Error in api calling enable nursing home");
          })
          .finally(() => {
            setLoadingEnableNursingHome(false);
          });
      } catch (error) {
        console.error("Error calling enable nursing home", error);
        toast.error("Error calling enable nursing home");
        setLoadingEnableNursingHome(false);
      }
    }
  };

  const handleDisableNursingHome = () => {
    setLoadingDisableNursingHome(true);
    try {
      enabledisableNursingHomeTrigger({
        location_id: currentLocation?.id,
        type:"careco",
        enable: 0
      })
        .then((res) => {
          if (res?.data?.data?.location?.Location?.careco_chat_enabled == "0") {
            refetchLocations();
            toast.success("Nursing Home disabled successfully");
            setNursingHomeChatEnabled(false);
          } else {
            toast.error("Error in disabling nursing home");
          }
        })
        .catch((err) => {
          console.log("Error in api calling disable nursing home", err);
          toast.error("Error in api calling disable nursing home");
        })
        .finally(() => {
          setLoadingDisableNursingHome(false);
          setShowTurnOffGroupDialogue(false);
        });
    } catch (error) {
      console.log("Error calling disable nursing home", error);
      toast.error("Error calling disable nursing home");
      setLoadingDisableNursingHome(false);
    }
  };

  return (
    <MainPageDialog
      title={`Care-Co Settings - ${currentLocation?.name || "Location"}`}
      footerList={footerList}
      footer={true}
      onCancelHandler={() =>
        setSelectedLocation({
          showSettingsDialogue: false,
          currentLocation: {},
          settingsType: "",
        })
      }
      contentCls="p-3 sm:p-4 md:px-6"
    >
      {selectedIp ?
        <ActionDialog
          onCancelHandler={() => setSelectedIp(null)}
          setShowModal={() => setSelectedIp(null)}
          onConfirmHandler={() => handleDeleteRestrictedIp(selectedIp)}
          confirmButtonText={deletingIp ? "Deleting" : "Yes"}
          cancelButtonText="No"
          showModal={selectedIp ? true : false}
          title={`Are you sure you want to delete selected Ip ${selectedIp?.ip} ?`}
          headerTitle="Delete IP"
        />
      : null}
      {showTurnOffGroupDialogue ? (
        <ActionDialog
          headerTitle="Location Group"
          onCancelHandler={() => setShowTurnOffGroupDialogue(false)}
          onConfirmHandler={() => handleDisableNursingHome()}
          showTitle={false}
          confirmButtonText={loadingDisableNursingHome ? "Disabling..." : "Yes"}
          cancelButtonText="No"
          showModal={showTurnOffGroupDialogue}
          setShowModal={() => setShowTurnOffGroupDialogue(false)}
          modalSize="md"
        >
          <div className="flex items-start gap-2 my-2 text-sm text-gray-700">
            <IoMdInformationCircleOutline className="shrink-0 mt-0.5" color="#656565" size={18} />
            <span>Turning OFF Location Group will clear all the Chat. Are you sure you want to proceed?</span>
          </div>
        </ActionDialog>
      ) : null}
      <div className="flex flex-col min-w-0">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 mb-4 md:mb-6 border border-gray-200 rounded-xl bg-slate-50/80 p-4 sm:p-5 shadow-sm">
          {/* Location info & Group status */}
          <div className="md:col-span-4 min-w-0 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <span className="text-sm font-semibold text-gray-600 shrink-0">Location:</span>
              <p className="text-sm text-gray-900 break-words min-w-0">{currentLocation?.name || "—"}</p>
            </div>

            {authData?.is_practice_admin && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <span className="text-sm font-semibold text-gray-600 shrink-0">Careco Status</span>
                <button
                  type="button"
                  aria-label={`Turn ${nursingHomeChatEnabled ? "OFF" : "ON"} Group`}
                  title={`Turn ${nursingHomeChatEnabled ? "OFF" : "ON"} Group`}
                  onClick={handleOpenTurnOffGroupDialogue}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleOpenTurnOffGroupDialogue(); }}
                  className="flex gap-1.5 justify-center items-center bg-[#3cc9b0] text-white py-2 px-4 min-w-[100px] rounded-lg hover:bg-[#2eb39d] focus:outline-none focus:ring-2 focus:ring-[#3cc9b0] focus:ring-offset-1 text-sm font-medium transition-colors shadow-sm"
                >
                  <span>
                    {nursingHomeChatEnabled ? "On" : loadingEnableNursingHome ? "Enabling..." : "Off"}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Restricted IPs */}
          {authData?.is_practice_admin && (
            <div className="md:col-span-8 min-w-0">
              <p className="text-sm font-semibold text-gray-600 mb-3 text-left">Restricted access to trusted IPs only</p>
              <div className="flex flex-wrap gap-2">
                {(restrictedIpsList || []).map((ip, index) => (
                  <div
                    key={ip?.id ?? index}
                    className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1.5 rounded-full bg-gray-200 text-gray-800 text-sm"
                  >
                    <span className="max-w-[120px] sm:max-w-[180px] truncate" title={ip.ip}>{ip.ip}</span>
                    <button
                      type="button"
                      aria-label={`Remove IP ${ip.ip}`}
                      className="flex items-center justify-center shrink-0 w-8 h-8 min-w-[24px] min-h-[24px] rounded-full hover:bg-gray-300 text-gray-600 hover:text-gray-800 transition-colors touch-manipulation"
                      onClick={() => setSelectedIp(ip)}
                    >
                      <IoIosClose className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                {!showAddIPField && (
                  <button
                    type="button"
                    onClick={() => setShowAddIPField(true)}
                    className="flex items-center justify-center w-8 h-8 min-w-[24px] min-h-[24px] rounded-full bg-[#3cc9b0] text-white hover:bg-[#2eb39d] focus:outline-none focus:ring-2 focus:ring-[#3cc9b0] focus:ring-offset-1 transition-colors shadow-sm touch-manipulation"
                    aria-label="Add IP"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
              {showAddIPField && (
                <form
                  className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 mt-3"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="w-full sm:w-[200px] md:w-[220px] min-w-0 flex-1 sm:flex-initial">
                    <InputField
                      name="ip"
                      placeholder="Enter IP address"
                      control={control}
                      errors={errors}
                      disabled={addingIp || deletingIp}
                      isDisabled={addingIp || deletingIp}
                      rules={{
                        required: { value: false },
                        validate: (value) => {
                          const isValid = validateIPInput(value);
                          if (isValid === true) {
                            if (restrictedIpsList?.find((ip) => ip.ip === value)) return "IP already exists";
                            return true;
                          }
                          return isValid;
                        },
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={addingIp || deletingIp}
                      className="flex items-center justify-center gap-1.5 bg-[#3cc9b0] text-white rounded-lg px-3 py-2.5 min-h-[28px] hover:bg-[#2eb39d] focus:outline-none focus:ring-2 focus:ring-[#3cc9b0] focus:ring-offset-1 disabled:opacity-50 text-sm font-medium transition-colors touch-manipulation"
                    >
                      <BsCheckLg className="w-4 h-4" />
                      <span>Add</span>
                    </button>
                    <button
                      type="button"
                      disabled={addingIp || deletingIp}
                      onClick={() => { setShowAddIPField(false); setValue("ip", ""); }}
                      className="flex items-center justify-center w-8 h-8 min-w-[32px] min-h-[32px] rounded-lg bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-50 transition-colors touch-manipulation"
                      aria-label="Cancel add IP"
                    >
                      <IoIosClose className="w-5 h-5" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
      {nursingHomeChatEnabled && (
        <>
          <LocationGroupNurses selectedLocation={selectedLocation} scrollToTop={scrollToTop} />
          {authData?.is_provider && (
            <LocationGroupUsers
              providersData={providersData || []}
              currentLocation={currentLocation}
              locationsData={locationsData || []}
            />
          )}
          <div className="flex flex-col gap-2 border-2 border-gray-100 rounded-md bg-slate-50 p-2 mt-4">
            <div className="p-2 bg-bgGreen text-white shadow-md flex items-center justify-between">
              <p className="text-left">Configurations</p>
            </div>
            <div className="border border-teal-500 rounded-md p-3 bg-white">
              <p className="text-left text-sm font-semibold mb-2">Select modules to enable for Nursing Staff</p>
              <div className="flex flex-col items-start gap-2">
                {locationConfigurationOptions.map((option) => (
                  <label key={option.key} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={toFlag(locationConfiguration[option.key]) === 1}
                      disabled={fetchingLocationConfiguration || savingLocationConfiguration}
                      onChange={(event) => handleLocationConfigurationChange(option.key, event.target.checked)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                disabled={fetchingLocationConfiguration || savingLocationConfiguration}
                onClick={handleSaveLocationConfiguration}
                className="bg-teal-500 text-white py-2 px-6 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50 disabled:opacity-50 text-sm font-medium"
              >
                {savingLocationConfiguration ? "Saving..." : "Save Configurations"}
              </button>
            </div>
          </div>
        </>
      )}
    </MainPageDialog>
  );
}

export default CareCoSettingsDialogue;
